import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WarehouseResponse,
  type WmCycleCount,
  type WmCycleCountLine,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProductSelect } from '@/components/selects';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { usePermissions } from '@/hooks/usePermissions';
import { useProductOptions } from '@/hooks/useProductOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function WarehouseCycleCountsPage() {
  const { hasAny } = usePermissions();
  const canAdmin = hasAny('WAREHOUSE_ADMIN');
  const canApprove = hasAny('WAREHOUSE_APPROVE', 'WAREHOUSE_ADMIN');
  const { getLabel: getProductLabel } = useProductOptions();
  const [counts, setCounts] = useState<WmCycleCount[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [selectedCountId, setSelectedCountId] = useState<number | null>(null);
  const [lines, setLines] = useState<WmCycleCountLine[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [productId, setProductId] = useState('');
  const [physicalQty, setPhysicalQty] = useState('');

  const reload = useCallback(() => {
    warehouseApi.listActiveCycleCounts().then(setCounts).catch((e) => showApiError(e, 'Failed to load counts'));
  }, []);

  useEffect(() => {
    reload();
    warehouseApi.listWarehouses().then(setWarehouses).catch(() => {});
  }, [reload]);

  const loadLines = async (id: number) => {
    setSelectedCountId(id);
    try {
      const l = await warehouseApi.listCycleCountLines(id);
      setLines(l);
    } catch (e) {
      showApiError(e, 'Failed to load count lines');
    }
  };

  const createCount = async () => {
    if (!warehouseId) return;
    try {
      const body: Parameters<typeof warehouseApi.createCycleCount>[0] = {
        warehouseId: Number(warehouseId),
        frequency: 'MONTHLY',
      };
      if (productId) {
        body.lines = [{ productId: Number(productId) }];
      }
      await warehouseApi.createCycleCount(body);
      notify.success('Cycle count created');
      reload();
    } catch (e) {
      showApiError(e, 'Create cycle count failed');
    }
  };

  const recordLine = async (lineId: number) => {
    if (!physicalQty) return;
    try {
      await warehouseApi.recordCycleCount(lineId, Number(physicalQty));
      notify.success('Count recorded');
      if (selectedCountId) loadLines(selectedCountId);
      reload();
    } catch (e) {
      showApiError(e, 'Record count failed');
    }
  };

  return (
    <div className="pb-safe">
      <PageHeader
        title="Cycle counts"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Cycle counts' },
        ]}
        actions={<Button variant="secondary" onClick={reload}>Refresh</Button>}
      />

      <div className="card mb-6 p-4">
        <h2 className="mb-3 font-semibold">New cycle count</h2>
        {canAdmin ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Select
                label="Warehouse"
                value={warehouseId}
                onChange={(v) => setWarehouseId(v)}
                options={[
                  { value: '', label: 'Select…' },
                  ...warehouses.map((w) => ({ value: String(w.id), label: w.code })),
                ]}
              />
              <ProductSelect
                label="Product (optional line)"
                value={productId}
                onChange={setProductId}
              />
            </div>
            <Button className="mt-3" onClick={() => void createCount()}>
              Create count
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-500">Warehouse admin permission required to create cycle counts.</p>
        )}
      </div>

      {counts.length === 0 ? (
        <p className="text-sm text-slate-500">No active cycle counts. Create one to begin.</p>
      ) : (
      <ul className="mb-6 space-y-2">
        {counts.map((c) => (
          <li key={c.id} className="card flex flex-wrap items-center justify-between gap-2 p-3">
            <button type="button" className="text-left" onClick={() => void loadLines(c.id)}>
              <span className="font-medium">{c.countCode}</span>
              <span className="ml-2 text-xs text-slate-500">
                WH {c.warehouseId} · {c.statusCode}
              </span>
            </button>
            <div className="flex gap-2">
              {c.statusCode === 'PLANNED' && canAdmin && (
                <Button size="sm" onClick={() => void warehouseApi.startCycleCount(c.id).then(reload)}>
                  Start
                </Button>
              )}
              {c.statusCode === 'IN_PROGRESS' && canAdmin && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    void warehouseApi.completeCycleCount(c.id).then(() => {
                      notify.success('Cycle count completed');
                      reload();
                      setLines([]);
                    })
                  }
                >
                  Complete
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      )}

      {selectedCountId && (
        <div className="card p-4">
          <h2 className="mb-3 font-semibold">Lines — count #{selectedCountId}</h2>
          <Input
            className="mb-3 max-w-xs"
            label="Physical quantity"
            value={physicalQty}
            onChange={(e) => setPhysicalQty(e.target.value)}
          />
          <ul className="space-y-2 text-sm">
            {lines.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2">
                {getProductLabel(l.productId, true)} · system {l.systemQuantity}
                {l.physicalQuantity != null ? ` · counted ${l.physicalQuantity}` : ''}
                {l.varianceQuantity != null ? ` · variance ${l.varianceQuantity}` : ''}
                {canAdmin && (
                  <Button size="sm" onClick={() => void recordLine(l.id)}>
                    Record
                  </Button>
                )}
                {l.approvalStatus === 'PENDING' && canApprove && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      void warehouseApi.approveCycleVariance(l.id).then(() => loadLines(selectedCountId))
                    }
                  >
                    Approve variance
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
